import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { CONTACT, PROFILE, REALISATIONS, SERVICES, realisationDetail } from "../config/routes";
import { projects as projectsData } from "../data/projectsData";
import "./styles/Home.css";

import agencyBackgroundPng from "../images/png/background/agency_background.png";
import agencyBackgroundAvif from "../images/avif/background/agency_background.avif";
import agencyBackgroundWebp from "../images/webp/background/agency_background.webp";

const featuredProjectSlugs = ["autogestia", "relyo", "hyroos", "echo-review"];

const projectProofs = {
  autogestia: "SaaS métier, données, automatisations et logique produit.",
  relyo: "Plateforme avec rôles, profils, workflows et backend structuré.",
  hyroos: "Application mobile avec caméra, scoring et pipeline technique.",
  "echo-review": "SaaS B2B avec dashboard, paiement et intégrations Google.",
};

const capabilityCards = [
  {
    title: "Frontend & interfaces",
    text: "Interfaces React/Vite, dashboards, parcours responsives et écrans de gestion lisibles.",
    stack: ["React", "Vite", "TypeScript", "Dashboards"],
  },
  {
    title: "Backend & données",
    text: "API Node.js, logique métier, auth, bases MySQL/PostgreSQL et modèles de données maintenables.",
    stack: ["Node.js", "Express", "API", "SQL"],
  },
  {
    title: "Mobile & produit",
    text: "Applications Flutter ou React Native reliées à un backend, Firebase ou Supabase selon le besoin.",
    stack: ["Flutter", "React Native", "Firebase", "Supabase"],
  },
  {
    title: "Intégrations & déploiement",
    text: "Paiements, OAuth, emails, notifications, Nginx, PM2, SSL et maintenance de production.",
    stack: ["Stripe", "OAuth", "Gmail API", "Nginx"],
  },
];

const serviceCards = [
  {
    title: "Applications web & SaaS",
    text: "Construire une application métier, un MVP SaaS ou un dashboard exploitable au quotidien.",
    deliverables: ["Interface React", "Backend API", "Base de données", "Déploiement"],
  },
  {
    title: "Applications mobiles",
    text: "Créer une app mobile reliée à des comptes, données, notifications ou services externes.",
    deliverables: ["Flutter / React Native", "Backend", "Push", "Publication selon périmètre"],
  },
  {
    title: "Dashboards & outils métiers",
    text: "Remplacer des fichiers dispersés ou processus manuels par un outil clair et maintenable.",
    deliverables: ["Tableaux de bord", "Rôles", "Exports", "Workflows"],
  },
  {
    title: "Automatisations & API",
    text: "Connecter des services, automatiser des tâches et fiabiliser les échanges de données.",
    deliverables: ["Intégrations API", "Emails", "Paiements", "Notifications"],
  },
];

const methodSteps = [
  {
    number: "01",
    title: "Comprendre le besoin métier",
    text: "Clarifier les utilisateurs, les données, les contraintes et les priorités avant de produire des écrans.",
  },
  {
    number: "02",
    title: "Concevoir une solution maintenable",
    text: "Choisir une architecture simple, éviter le scope flou et préparer les évolutions réalistes.",
  },
  {
    number: "03",
    title: "Développer le produit complet",
    text: "Frontend, backend, base de données, API et intégrations sont pensés ensemble.",
  },
  {
    number: "04",
    title: "Déployer et maintenir",
    text: "Mise en ligne, configuration serveur, corrections et suivi technique selon le périmètre.",
  },
];

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="home5-sectionHead">
      <p className="home5-eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

function ChipList({ items }) {
  return (
    <div className="home5-chips">
      {items.map((item) => (
        <span className="home5-chip" key={item}>
          {item}
        </span>
      ))}
    </div>
  );
}

function ProjectCard({ project }) {
  return (
    <article className="home5-projectCard">
      <div className="home5-projectTop">
        <h3>{project.shortTitle || project.title}</h3>
        <Link to={realisationDetail(project.slug)} className="home5-textLink">
          Voir le projet
        </Link>
      </div>
      <p>{project.summary || project.subtitle}</p>
      <div className="home5-proof">
        <strong>Ce que ça démontre :</strong> {projectProofs[project.slug]}
      </div>
      <ChipList items={(project.stack || []).slice(0, 4)} />
    </article>
  );
}

export default function HomeV5() {
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 599 : false
  );

  const siteUrl = "https://dreameragency.fr/";
  const metaTitle = "Dreamer Agency - Applications web, SaaS et solutions métiers";
  const metaDesc =
    "Développement d’applications web, SaaS, mobiles et solutions métiers sur mesure : frontend, backend, bases de données, API, intégrations et déploiement.";
  const ogImage = "https://dreameragency.fr/media/OG/agency_background.png";

  const featuredProjects = useMemo(
    () =>
      featuredProjectSlugs
        .map((slug) => projectsData.find((project) => project.slug === slug))
        .filter(Boolean),
    []
  );

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Dreamer Agency",
        url: siteUrl,
        logo: "https://dreameragency.fr/media/OG/DA_logo_background.png",
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Dreamer Agency",
        url: siteUrl,
      },
    ],
    [siteUrl]
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();
    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }
    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".home5");
    if (!mainContainer) return;
    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  return (
    <main className={`home5 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={siteUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <section className="home5-hero">
        <picture className="home5-heroMedia">
          <source srcSet={agencyBackgroundAvif} type="image/avif" />
          <source srcSet={agencyBackgroundWebp} type="image/webp" />
          <img src={agencyBackgroundPng} alt="" />
        </picture>
        <div className="home5-heroOverlay" />

        <div className="home5-container home5-heroContent">
          <p className="home5-eyebrow">Dreamer Agency</p>
          <h1>Applications web, SaaS et solutions métiers sur mesure</h1>
          <p className="home5-lead">
            Conception, développement et déploiement de produits digitaux complets :
            interfaces web, backend, bases de données, API, intégrations, automatisations
            et maintenance.
          </p>
          <p className="home5-heroNote">
            Une approche full-stack portée par Florian Da Cunha, développeur basé à Toulouse.
          </p>

          <div className="home5-actions">
            <Link to={REALISATIONS} className="home5-btn home5-btnPrimary">
              Voir les réalisations
            </Link>
            <Link to={CONTACT} className="home5-btn home5-btnSecondary">
              Discuter d’un projet
            </Link>
            <Link to={PROFILE} className="home5-profileLink">
              Profil développeur
            </Link>
          </div>
        </div>
      </section>

      <section className="home5-section">
        <div className="home5-container">
          <SectionHeading
            eyebrow="Preuves techniques"
            title="Un produit complet, ce n’est pas seulement une interface."
          >
            Les projets livrés couvrent l’interface, le backend, les données, les API,
            les intégrations et la mise en ligne.
          </SectionHeading>

          <div className="home5-capabilityGrid">
            {capabilityCards.map((card) => (
              <article className="home5-card" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
                <ChipList items={card.stack} />
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home5-section home5-sectionAlt">
        <div className="home5-container">
          <SectionHeading eyebrow="Réalisations" title="Projets phares">
            Quelques projets représentatifs de la capacité à concevoir et livrer des
            applications web, SaaS, mobiles ou métiers.
          </SectionHeading>

          <div className="home5-projectGrid">
            {featuredProjects.map((project) => (
              <ProjectCard project={project} key={project.slug} />
            ))}
          </div>

          <div className="home5-sectionCta">
            <Link to={REALISATIONS} className="home5-btn home5-btnPrimary">
              Voir toutes les réalisations
            </Link>
          </div>
        </div>
      </section>

      <section className="home5-section">
        <div className="home5-container">
          <SectionHeading eyebrow="Services" title="Des prestations concrètes, centrées livraison">
            L’objectif : cadrer le bon périmètre, construire proprement et garder une base
            maintenable après la mise en ligne.
          </SectionHeading>

          <div className="home5-serviceGrid">
            {serviceCards.map((service) => (
              <article className="home5-serviceCard" key={service.title}>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
                <ul>
                  {service.deliverables.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link to={SERVICES} className="home5-textLink">
                  Voir les services
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home5-section home5-sectionAlt">
        <div className="home5-container">
          <SectionHeading eyebrow="Méthode" title="Une façon de travailler lisible">
            Une méthode sobre pour éviter les effets tunnel et garder le lien entre besoin
            métier, architecture technique et livraison.
          </SectionHeading>

          <div className="home5-methodGrid">
            {methodSteps.map((step) => (
              <article className="home5-methodCard" key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home5-section">
        <div className="home5-container">
          <div className="home5-pathGrid">
            <article className="home5-pathCard">
              <p className="home5-eyebrow">Projet client</p>
              <h2>Vous avez un projet web, SaaS ou mobile ?</h2>
              <p>
                Parlons du besoin, du périmètre, des risques techniques et de la première
                version utile à livrer.
              </p>
              <Link to={CONTACT} className="home5-btn home5-btnPrimary">
                Me contacter
              </Link>
            </article>

            <article className="home5-pathCard">
              <p className="home5-eyebrow">Profil développeur</p>
              <h2>Vous recrutez un développeur full-stack ?</h2>
              <p>
                Le profil de Florian détaille son parcours, sa stack, ses expériences ERP
                et freelance, ainsi que les projets les plus crédibles.
              </p>
              <Link to={PROFILE} className="home5-btn home5-btnSecondary">
                Voir le profil développeur
              </Link>
            </article>
          </div>
        </div>
      </section>

      <section className="home5-finalCta">
        <div className="home5-container home5-finalInner">
          <div>
            <p className="home5-eyebrow">Point de départ</p>
            <h2>Regarder les preuves, puis décider quoi construire.</h2>
            <p>
              Les réalisations montrent le type de produits construits. Le contact permet
              ensuite de cadrer calmement un besoin réel.
            </p>
          </div>
          <div className="home5-finalActions">
            <Link to={REALISATIONS} className="home5-btn home5-btnPrimary">
              Voir les réalisations
            </Link>
            <Link to={CONTACT} className="home5-btn home5-btnSecondary">
              Me contacter
            </Link>
          </div>
        </div>
      </section>

      {jsonLd.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </main>
  );
}
